
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Users, Shield, List, LockIcon, Search, Ban, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserProfile, UserRole } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserCreateDialog } from './user/UserCreateDialog';
import { UserEditDialog } from './user/UserEditDialog';
import { UserDeleteDialog } from './user/UserDeleteDialog';
import { UserBlockDialog } from './user/UserBlockDialog';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@/components/ui/pagination';

export default function UsersSettings() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  
  // Поиск и пагинация
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const pageSize = 10; // Фиксированный размер страницы
  
  // Применяем дебаунсинг к поисковому запросу
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Загрузка пользователей напрямую из Supabase
  const loadUsers = async () => {
    setIsLoading(true);
    try {
      // Начинаем запрос к таблице profiles
      let query = supabase.from('profiles').select('*', { count: 'exact' });

      // Добавляем поиск, если задан запрос
      if (debouncedSearchTerm) {
        const searchFields = ['name', 'phone', 'email'];
        // Создаем поиск по нескольким полям через .or
        query = query.or(
          searchFields.map(field => `${field}.ilike.%${debouncedSearchTerm}%`).join(',')
        );
      }
      
      // Определяем диапазон для пагинации
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // Применяем пагинацию
      query = query.range(from, to);
      
      // Выполняем запрос
      const { data, count, error } = await query;
      
      if (error) throw error;
      
      // Преобразуем данные с проверкой типа роли
      const typedUsers: UserProfile[] = data?.map(user => {
        // Проверяем что роль соответствует UserRole (admin или courier)
        const role: UserRole = (user.role === 'admin' ? 'admin' : 'courier') as UserRole;
        
        return {
          id: user.id,
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          role: role,
          avatar: user.avatar || '',
          created_at: user.created_at,
          updated_at: user.updated_at,
          is_blocked: user.is_blocked || false
        } as UserProfile & { is_blocked: boolean };
      }) || [];
      
      setUsers(typedUsers);
      setTotalUsers(count || 0);
    } catch (error: any) {
      toast.error(`Ошибка при загрузке пользователей: ${error.message}`);
      console.error('Ошибка загрузки пользователей:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Перезагрузка при изменении поисковых параметров или страницы
  useEffect(() => {
    loadUsers();
  }, [debouncedSearchTerm, currentPage]);

  const handleEdit = (user: UserProfile) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (user: UserProfile) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleBlock = (user: UserProfile) => {
    setSelectedUser(user);
    setIsBlockDialogOpen(true);
  };
  
  // Обработчики пагинации
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    const totalPages = Math.ceil(totalUsers / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Переход на конкретную страницу
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };
  
  // Формирование элементов пагинации
  const renderPaginationItems = () => {
    const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));
    
    // Если страниц мало, показываем все
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => (
        <PaginationItem key={i + 1}>
          <PaginationLink 
            isActive={currentPage === i + 1}
            onClick={() => goToPage(i + 1)}
          >
            {i + 1}
          </PaginationLink>
        </PaginationItem>
      ));
    }
    
    // Иначе показываем текущую, первую, последнюю и соседние
    const items = [];
    
    // Первая страница
    items.push(
      <PaginationItem key={1}>
        <PaginationLink 
          isActive={currentPage === 1}
          onClick={() => goToPage(1)}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );
    
    // Многоточие в начале
    if (currentPage > 3) {
      items.push(<PaginationItem key="ellipsis-start">...</PaginationItem>);
    }
    
    // Страницы вокруг текущей
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink 
            isActive={currentPage === i}
            onClick={() => goToPage(i)}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    // Многоточие в конце
    if (currentPage < totalPages - 2) {
      items.push(<PaginationItem key="ellipsis-end">...</PaginationItem>);
    }
    
    // Последняя страница
    if (totalPages > 1) {
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink 
            isActive={currentPage === totalPages}
            onClick={() => goToPage(totalPages)}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Управление пользователями</h2>
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          className="flex items-center gap-2"
        >
          <Users className="h-4 w-4" />
          Добавить пользователя
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Список пользователей
          </CardTitle>
          <CardDescription>
            Управление пользователями и их ролями в системе
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Единое поле поиска пользователей */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Поиск по имени, телефону или email"
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-4">Загрузка пользователей...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                {debouncedSearchTerm
                  ? 'Пользователи не найдены по заданному запросу' 
                  : 'Нет пользователей для отображения'}
              </div>
            ) : (
              <div className="divide-y">
                {users.map(user => (
                  <div key={user.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={user.avatar} />
                        <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{user.name}</p>
                          {(user as any).is_blocked && (
                            <span title="Пользователь заблокирован">
                              <Ban className="h-4 w-4 text-red-500" />
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{user.email || user.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.role === 'admin' 
                          ? 'bg-primary/10 text-primary' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {user.role === 'admin' ? 'Администратор' : 'Курьер'}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEdit(user)}
                      >
                        Изменить
                      </Button>
                      <Button 
                        variant={(user as any).is_blocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleBlock(user)}
                        className="min-w-[110px]"
                      >
                        {(user as any).is_blocked ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Разблокировать
                          </>
                        ) : (
                          <>
                            <Ban className="h-3 w-3 mr-1" />
                            Заблокировать
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDelete(user)}
                      >
                        Удалить
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Пагинация */}
            {!isLoading && totalUsers > pageSize && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={handlePreviousPage}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    
                    {renderPaginationItems()}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={handleNextPage}
                        className={currentPage >= Math.ceil(totalUsers / pageSize) ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
                <div className="text-center text-sm text-muted-foreground mt-2">
                  Страница {currentPage} из {Math.ceil(totalUsers / pageSize)}, 
                  всего пользователей: {totalUsers}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Диалоги */}
      <UserCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={loadUsers}
      />

      {selectedUser && (
        <>
          <UserEditDialog
            isOpen={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            onSuccess={loadUsers}
            user={selectedUser}
          />
          <UserDeleteDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onSuccess={loadUsers}
            user={selectedUser}
          />
          <UserBlockDialog
            isOpen={isBlockDialogOpen}
            onClose={() => setIsBlockDialogOpen(false)}
            onSuccess={loadUsers}
            user={selectedUser}
          />
        </>
      )}

      {/* Остальные карточки с информацией о правах */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <LockIcon className="h-5 w-5 mr-2" />
            Управление доступом
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-md border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-primary" />
                <span className="font-medium">Права администратора</span>
              </div>
              <ul className="list-disc ml-6 text-sm text-muted-foreground">
                <li>Полный доступ ко всем разделам системы</li>
                <li>Управление пользователями и их ролями</li>
                <li>Доступ к настройкам системы</li>
                <li>Управление велосипедами, арендами и платежами</li>
                <li>Доступ к аналитическим данным</li>
              </ul>
            </div>
            
            <div className="rounded-md border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Права курьера</span>
              </div>
              <ul className="list-disc ml-6 text-sm text-muted-foreground">
                <li>Доступ к личному профилю</li>
                <li>Просмотр личных аренд и платежей</li>
                <li>Управление личными настройками</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <List className="h-5 w-5 mr-2" />
            Логи активности
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            История действий пользователей в системе. Активность отслеживается для всех учетных записей.
          </p>
          <Button variant="outline">Просмотреть журнал активности</Button>
        </CardContent>
      </Card>
    </div>
  );
}
